import React, { useRef, useState, useEffect } from 'react';

// Tag disponibili con descrizioni
const AVAILABLE_TAGS = {
  // Fornitore
  '{FORNITORE}': 'Nome del fornitore',
  '{FORNITORE_EMAIL}': 'Email fornitore',
  '{FORNITORE_TELEFONO}': 'Telefono fornitore',
  '{FORNITORE_REFERENTE}': 'Nome referente fornitore',

  // RSA
  '{RSA}': 'Nome RSA',
  '{RSA_INDIRIZZO}': 'Indirizzo RSA',
  '{RSA_TELEFONO}': 'Telefono RSA',
  '{RSA_DIRETTORE}': 'Nome direttore RSA',
  '{RSA_EMAIL_DIREZIONE}': 'Email direzione RSA',

  // Tipologie
  '{TIPOLOGIA_INTERVENTO}': 'Tipologia intervento',
  '{TIPOLOGIA_FORNITURA}': 'Tipologia fornitura',

  // Date Scadenza
  '{DATA_SCADENZA}': 'Data scadenza (DD/MM/YYYY)',
  '{DATA_SCADENZA_ESTESA}': 'Data scadenza estesa',
  '{GIORNI_MANCANTI}': 'Giorni alla scadenza',
  '{GIORNI_MANCANTI_TESTO}': 'Giorni in testo (es: "tra 7 giorni")',

  // Date Generali
  '{DATA_OGGI}': 'Data odierna',
  '{DATA_OGGI_ESTESA}': 'Data odierna estesa',

  // Contratto
  '{NOME_CONTRATTO}': 'Nome contratto',
  '{DATA_INIZIO_CONTRATTO}': 'Data inizio contratto',
  '{DATA_FINE_CONTRATTO}': 'Data fine contratto',
  '{IMPORTO_CONTRATTO}': 'Importo contratto',

  // Sistema
  '{LINK_PORTALE}': 'URL portale web',
  '{LINK_DETTAGLIO}': 'Link diretto al dettaglio',
  '{AZIENDA}': 'Nome azienda',
  '{ANNO_CORRENTE}': 'Anno corrente'
};

// Raggruppa i tag per categoria
const TAG_CATEGORIES = {
  'Fornitore': ['{FORNITORE}', '{FORNITORE_EMAIL}', '{FORNITORE_TELEFONO}', '{FORNITORE_REFERENTE}'],
  'RSA': ['{RSA}', '{RSA_INDIRIZZO}', '{RSA_TELEFONO}', '{RSA_DIRETTORE}', '{RSA_EMAIL_DIREZIONE}'],
  'Scadenza': ['{DATA_SCADENZA}', '{DATA_SCADENZA_ESTESA}', '{GIORNI_MANCANTI}', '{GIORNI_MANCANTI_TESTO}'],
  'Contratto': ['{NOME_CONTRATTO}', '{DATA_INIZIO_CONTRATTO}', '{DATA_FINE_CONTRATTO}', '{IMPORTO_CONTRATTO}'],
  'Tipologie': ['{TIPOLOGIA_INTERVENTO}', '{TIPOLOGIA_FORNITURA}'],
  'Sistema': ['{DATA_OGGI}', '{LINK_PORTALE}', '{LINK_DETTAGLIO}', '{AZIENDA}', '{ANNO_CORRENTE}']
};

/**
 * Editor di template email con supporto per tag dinamici
 */
function TemplateEditor({
  value = '',
  onChange,
  label,
  placeholder = 'Scrivi il contenuto del template...',
  rows = 12,
  showPreview = true,
  isHtml = false,
  previewData = {},
  error,
  helperText,
  className = ''
}) {
  const textareaRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Fornitore');
  const [searchTag, setSearchTag] = useState('');

  // Inserisce un tag alla posizione del cursore
  const insertTag = (tag) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const before = value.substring(0, cursorPosition);
    const after = value.substring(cursorPosition);
    const newValue = before + tag + after;

    onChange(newValue);

    // Riposiziona il cursore dopo il tag
    setTimeout(() => {
      textarea.focus();
      const newPosition = cursorPosition + tag.length;
      textarea.setSelectionRange(newPosition, newPosition);
      setCursorPosition(newPosition);
    }, 0);

    setShowTagPicker(false);
  };

  // Aggiorna posizione cursore
  const handleSelect = (e) => {
    setCursorPosition(e.target.selectionStart);
  };

  // Evidenzia i tag nel testo per la preview
  const highlightTags = (text) => {
    if (!text) return '';
    return text.replace(
      /\{([A-Z_]+)\}/g,
      '<span class="bg-yellow-200 text-yellow-800 px-1 rounded text-sm font-mono">{$1}</span>'
    );
  };

  // Genera preview con dati di esempio o reali
  const generatePreview = (text) => {
    if (!text) return '';

    let result = text;

    // Dati di esempio di default
    const sampleData = {
      '{FORNITORE}': previewData.fornitore || 'Fornitore Esempio Srl',
      '{FORNITORE_EMAIL}': previewData.fornitoreEmail || 'info@fornitore.it',
      '{FORNITORE_TELEFONO}': '02 1234567',
      '{FORNITORE_REFERENTE}': 'Mario Rossi',
      '{RSA}': previewData.rsa || 'RSA Villa Serena',
      '{RSA_INDIRIZZO}': 'Via Roma 123, Milano',
      '{RSA_TELEFONO}': '02 9876543',
      '{RSA_DIRETTORE}': 'Dott. Bianchi',
      '{RSA_EMAIL_DIREZIONE}': 'direzione@rsa.it',
      '{TIPOLOGIA_INTERVENTO}': previewData.tipologiaIntervento || 'Manutenzione Ascensori',
      '{TIPOLOGIA_FORNITURA}': 'Manutenzione Impianti',
      '{DATA_SCADENZA}': previewData.dataScadenza || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('it-IT'),
      '{DATA_SCADENZA_ESTESA}': 'Lunedi 15 Gennaio 2026',
      '{GIORNI_MANCANTI}': '7',
      '{GIORNI_MANCANTI_TESTO}': 'tra 7 giorni',
      '{DATA_OGGI}': new Date().toLocaleDateString('it-IT'),
      '{DATA_OGGI_ESTESA}': new Date().toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      '{NOME_CONTRATTO}': 'Contratto Manutenzione 2026',
      '{DATA_INIZIO_CONTRATTO}': '01/01/2026',
      '{DATA_FINE_CONTRATTO}': '31/12/2026',
      '{IMPORTO_CONTRATTO}': '12.000,00',
      '{LINK_PORTALE}': 'https://portal.peralbahc.com',
      '{LINK_DETTAGLIO}': 'https://portal.peralbahc.com/scadenze/123',
      '{AZIENDA}': 'Peralba Health Care',
      '{ANNO_CORRENTE}': new Date().getFullYear().toString()
    };

    // Sostituisci tutti i tag
    for (const [tag, value] of Object.entries(sampleData)) {
      const escapedTag = tag.replace(/[{}]/g, '\\$&');
      result = result.replace(new RegExp(escapedTag, 'g'), value);
    }

    return result;
  };

  // Filtra tag per ricerca
  const filteredTags = searchTag
    ? Object.entries(AVAILABLE_TAGS).filter(([tag, desc]) =>
        tag.toLowerCase().includes(searchTag.toLowerCase()) ||
        desc.toLowerCase().includes(searchTag.toLowerCase())
      )
    : Object.entries(AVAILABLE_TAGS).filter(([tag]) =>
        TAG_CATEGORIES[selectedCategory]?.includes(tag)
      );

  return (
    <div className={`template-editor ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="border rounded-lg overflow-hidden bg-white">
        {/* Toolbar */}
        <div className="flex items-center gap-2 p-2 bg-gray-50 border-b">
          <button
            type="button"
            onClick={() => setShowTagPicker(!showTagPicker)}
            className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
              showTagPicker
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            } border`}
          >
            + Inserisci Tag
          </button>

          <span className="text-xs text-gray-500 ml-auto">
            Usa i tag per personalizzare il messaggio
          </span>
        </div>

        {/* Tag Picker */}
        {showTagPicker && (
          <div className="p-3 bg-gray-50 border-b">
            {/* Search */}
            <input
              type="text"
              value={searchTag}
              onChange={(e) => setSearchTag(e.target.value)}
              placeholder="Cerca tag..."
              className="w-full px-3 py-2 text-sm border rounded mb-3"
            />

            {/* Category tabs */}
            {!searchTag && (
              <div className="flex flex-wrap gap-1 mb-3">
                {Object.keys(TAG_CATEGORIES).map(category => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSelectedCategory(category)}
                    className={`px-2 py-1 text-xs rounded ${
                      selectedCategory === category
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            {/* Tag list */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {filteredTags.map(([tag, description]) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => insertTag(tag)}
                  className="text-left p-2 rounded bg-white hover:bg-primary-50 border hover:border-primary-300 transition-colors"
                >
                  <div className="font-mono text-xs text-primary-600">{tag}</div>
                  <div className="text-xs text-gray-500 truncate">{description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSelect={handleSelect}
          onClick={handleSelect}
          onKeyUp={handleSelect}
          rows={rows}
          placeholder={placeholder}
          className={`w-full p-4 font-mono text-sm resize-none focus:outline-none ${
            error ? 'bg-red-50' : ''
          }`}
        />

        {/* Preview */}
        {showPreview && value && (
          <div className="border-t">
            <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase">
                Anteprima
              </span>
              {isHtml && (
                <span className="text-xs text-blue-500">Rendering HTML</span>
              )}
            </div>
            <div className="p-4 bg-white overflow-auto max-h-96">
              {isHtml ? (
                <iframe
                  srcDoc={generatePreview(value)}
                  className="w-full border-0"
                  style={{ minHeight: '300px' }}
                  title="Anteprima HTML"
                  sandbox="allow-same-origin"
                />
              ) : (
                <div className="text-sm whitespace-pre-wrap">
                  {generatePreview(value)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Helper text / Error */}
      {(helperText || error) && (
        <p className={`mt-1 text-xs ${error ? 'text-red-500' : 'text-gray-500'}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
}

export default TemplateEditor;
export { AVAILABLE_TAGS, TAG_CATEGORIES };
